import { Box, FormControlLabel, FormGroup, Grid, Switch, TextField, Tooltip } from '@mui/material'
import { Dayjs } from 'dayjs'
import { ReactComponent as AccessibleIcon } from '../../../assets/icons/date-picker.svg'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider'
import { useContext } from 'react'
import { GlobalInfoContext } from 'contexts/GlobalStateContext'
import { FormikTouched, FormikValues } from 'formik'
import Item from 'pages/dashboard/sub-sections/items'
import {
  getMinExpiryDate,
  getMinExpiryDays,
  getExpiryDateErrorMessage
} from 'utils/expiryDateConfig'
import dayjs from 'dayjs'

export type Orgs = {
  label: string
  businessId: string
  country?: string
  displayId: string
  type: string
  commonName?: string | undefined
  legalEntityCodes?: [
    {
      legalEntityCodeType?: string
      legalEntityCodeData?: string
    }
  ]
  contactFirstName?: string | undefined
  contactLastName?: string | undefined
  address?: string | undefined
  phone?: string | undefined
  email?: string | undefined
}

type TimeRowProps = {
  startDateValue: Dayjs | null
  endDateValue: Dayjs | null
  maxDate: Dayjs | null
  minDate: Dayjs | null
  setDateValue: (value: any, type: string) => any
  openEnded: boolean
  setOpenEnded: any
  errors?: FormikValues
  touched?: FormikTouched<any>
  disablePast?: boolean
}
export const TimeRow = ({
  startDateValue,
  endDateValue,
  minDate,
  maxDate,
  setDateValue,
  errors,
  touched,
  disablePast
}: TimeRowProps) => {
  const { purpose } = useContext(GlobalInfoContext)

  // Validation handler for date input
  const validateAndSetDate = (newValue: any, dateType: string) => {
    if (!newValue) {
      setDateValue(newValue, dateType)
      return
    }

    // Check if date is past (for disablePast)
    if (disablePast && newValue.isBefore(dayjs(), 'day')) {
      return
    }

    // Check maxDate constraint for start date
    if (dateType === 'startDate' && maxDate && newValue.isAfter(maxDate, 'day')) {
      return
    }

    // Check minDate constraint for end date
    if (dateType === 'endDate' && minDate && newValue.isBefore(minDate, 'day')) {
      return
    }

    setDateValue(newValue, dateType)
  }

  return (
    <>
      <Grid item xs={3.94}>
        <Item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Contract Start Date"
              value={purpose?.contract?.startDate || startDateValue}
              inputFormat="DD/MM/YYYY"
              onChange={(newValue) => validateAndSetDate(newValue, 'startDate')}
              disablePast={disablePast ?? false}
              maxDate={maxDate}
              shouldDisableDate={(date) =>
                date ? (disablePast ?? false ? date.isBefore(dayjs(), 'day') : false) : false
              }
              renderInput={(params) => (
                <TextField
                  label="Contract Start Date"
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps }}
                  error={Boolean(touched?.startDate && errors?.startDate)}
                  helperText={touched?.startDate && errors?.startDate}
                />
              )}
              components={{
                OpenPickerIcon: AccessibleIcon
              }}
            />
          </LocalizationProvider>
        </Item>
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={4.06}>
        <Item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Contract End Date"
              value={purpose?.contract?.endDate || endDateValue || null}
              inputFormat="DD/MM/YYYY"
              onChange={(newValue) => validateAndSetDate(newValue, 'endDate')}
              minDate={minDate}
              disablePast={disablePast ?? false}
              shouldDisableDate={(date) =>
                date
                  ? disablePast ?? false
                    ? date.isBefore(dayjs(), 'day')
                    : minDate
                    ? date.isBefore(minDate, 'day')
                    : false
                  : false
              }
              components={{
                OpenPickerIcon: AccessibleIcon
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contract End Date"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps }}
                  error={Boolean(touched?.endDate && errors?.endDate)}
                  helperText={touched?.endDate && errors?.endDate}
                />
              )}
            />
          </LocalizationProvider>
        </Item>
      </Grid>
    </>
  )
}

export const ExpiryDate = ({
  expiryDateValue,
  setExpiryDate,
  openEnded,
  handleOpenEndedDate,
  errors,
  touched
}) => {
  const { expiryDate } = useContext(GlobalInfoContext)
  const value = expiryDate ? expiryDate : expiryDateValue
  const minExpiryDate = dayjs(getMinExpiryDate())
  const minDays = getMinExpiryDays()
  const tooltipMessage = `Minimum expiry date is ${minDays} day${minDays !== 1 ? 's' : ''}`

  // Function to check if a date is disabled (before minimum expiry date)
  const isDateDisabled = (date: Dayjs | null) => {
    if (!date) return false
    return date.isBefore(minExpiryDate, 'day') || date.isBefore(dayjs(), 'day')
  }

  // Custom day rendering with tooltip
  const renderDay = (
    day: Dayjs,
    selectedDays: Dayjs[],
    pickersDayProps: PickersDayProps<Dayjs>
  ) => {
    const isDisabled = isDateDisabled(day)
    const dateKey = day.format('YYYY-MM-DD')

    if (isDisabled) {
      return (
        <Tooltip key={dateKey} title={tooltipMessage} arrow>
          <span>
            <PickersDay {...pickersDayProps} disabled={true} />
          </span>
        </Tooltip>
      )
    }

    return <PickersDay key={dateKey} {...pickersDayProps} />
  }

  return (
    <>
      <Grid item xs={4} sx={{ paddingRight: '15px' }}>
        <Item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expiry Date (Mandatory)"
              value={value}
              data-testid="picker-expiry-date"
              disabled={openEnded}
              inputFormat="DD/MM/YYYY"
              disablePast={true}
              minDate={minExpiryDate}
              shouldDisableDate={(date) => isDateDisabled(date)}
              onChange={(newValue) => {
                // Only accept valid dates that meet minimum requirements
                if (!newValue) {
                  setExpiryDate(null, 'expiryDate')
                  return
                }
                // Validate against minimum expiry date
                if (newValue.isBefore(minExpiryDate, 'day')) {
                  // Don't update state - keep previous valid value
                  return
                }
                // Date is valid, update state
                setExpiryDate(newValue, 'expiryDate')
              }}
              onAccept={(newValue) => {
                // Additional validation on acceptance (when calendar is closed)
                if (!newValue) {
                  return
                }
                if (newValue.isBefore(minExpiryDate, 'day')) {
                  return
                }
                setExpiryDate(newValue, 'expiryDate')
              }}
              renderDay={renderDay}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Expiry Date"
                  id="expiry-date"
                  placeholder="Expiry Date (Mandatory)"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, readOnly: true }}
                  error={Boolean(touched?.expiryDate && errors?.expiryDate)}
                  helperText={touched?.expiryDate && errors?.expiryDate}
                  title={tooltipMessage}
                />
              )}
              components={{
                OpenPickerIcon: AccessibleIcon
              }}
            />
          </LocalizationProvider>
        </Item>
      </Grid>
      <Grid item md={1} lg={1} />
      <Grid item md={2} lg={2}>
        <Box>
          <FormGroup>
            <FormControlLabel
              id="no-expiry"
              control={
                <Switch checked={openEnded} onChange={() => handleOpenEndedDate(!openEnded)} />
              }
              label="No Expiry Date"
            />
          </FormGroup>
        </Box>
      </Grid>
    </>
  )
}
